import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Tooltip from '@mui/material/Tooltip';
import NRStatus from '../NRStatus/NRStatus';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import { getDataFromTurbinePPComposedAssetId, isEmpty, humanFileSize } from '../../utils/utils';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useContext, useEffect } from 'react';
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CONSTANTS, FEATURE_FLAGS } from '../../constants/constants';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import NRLoader from '../NRLoader/NRLoader';
import { ComponentContext } from '../../ContextWrappers/NRComponentContextWrapper';
import useNRLazyQuery from '../../graphql/useNRLazyQuery';
import { GET_ASSET } from '../../graphql/queries/asset';
import { Copy } from 'react-feather';
import { useSnackbar } from 'notistack';
import { ModalMainContainer } from '../NRActionModal/NRActionModal';
import { useFlag } from '@openfeature/react-sdk';

SyntaxHighlighter.registerLanguage('bash', bash);
const { CLOSE } = CONSTANTS.ACTIONS;
const { ANALYSIS_LOG, ANALYSIS_LOG_THRESHOLDS } = CONSTANTS.DATE_FORMAT;
const { IN_PROGRESS, COMPLETED, FAILED } = CONSTANTS.TABLE.ASSETS.STATUS;

function parsedAnalysisStatus(orgComponent) {
  function addSuccessfullyRan() {
    return `Analysis Successfully ran.${addDuration(orgComponent.createdAt, orgComponent.firstAnalysisTime, true)}`;
  }

  switch (orgComponent.status) {
    case COMPLETED:
      return addSuccessfullyRan();

    case IN_PROGRESS:
      if (!!orgComponent.firstAnalysisTime) {
        return addSuccessfullyRan();
      } else return `[ Analysis still running ] (started about ${moment(new Date(orgComponent.startTime || orgComponent.createdAt)).fromNow()}),`;

    default:
      //FAILED
      let toReturn = '';
      if (!!orgComponent.firstAnalysisTime) {
        toReturn = addSuccessfullyRan();
        toReturn += `Analysis updated at: ${moment(new Date(orgComponent.updatedAt))?.format(ANALYSIS_LOG)},`;
      }
      toReturn += !!orgComponent.endTime
        ? `Analysis ${orgComponent.status === FAILED ? 'failed' : orgComponent.status} at: ${moment(new Date(orgComponent.endTime))?.format(
            ANALYSIS_LOG
          )},` + `Details: FAIL`
        : 'Details: FAIL';
      return toReturn;
  }
}

function addDuration(startTime, endTime, newLineWhenNoDuration) {
  if (!startTime || !endTime) return !!newLineWhenNoDuration ? ',' : '';
  const duration = moment.duration(moment(new Date(endTime || Date.now())).diff(new Date(startTime))).humanize();
  return ` Duration: about ${duration},`;
}

function NRAnalysisLog({ componentId, open, handleClose }) {
  const theme = useTheme();
  const auth0Namespace = process.env.REACT_APP_AUTH0_NAMESPACE;
  const orgNamespace = auth0Namespace + 'org_name';
  const orgIdNamespace = auth0Namespace + 'org_id';
  const [org, setOrg] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [component, setComponent] = useState(null);
  const [parsedAnalysis, setParsedAnalysis] = useState({});
  const { getIdTokenClaims } = useAuth0();
  const { asset } = useContext(ComponentContext);
  const { enqueueSnackbar } = useSnackbar();
  // TODO: Remove feature flag checks and isPQCEnabled logic when certificates-pqc, public-key-pqc, and private-key-pqc feature flags are removed
  // Remove includeQuantumCapable from makeRequest variables and always query quantumCapable field directly
  const { value: isCertificatesPQCEnabled } = useFlag(FEATURE_FLAGS.CERTIFICATES_PQC, false);
  const { value: isPublicKeyPQCEnabled } = useFlag(FEATURE_FLAGS.PUBLIC_KEY_PQC, false);
  const { value: isPrivateKeyPQCEnabled } = useFlag(FEATURE_FLAGS.PRIVATE_KEYS_PQC, false);

  const isPQCEnabled = isCertificatesPQCEnabled || isPublicKeyPQCEnabled || isPrivateKeyPQCEnabled;

  const [assetData, , called, makeRequest] = useNRLazyQuery({
    query: GET_ASSET,
    options: {
      variables: { args: { assetId: componentId }, includeQuantumCapable: isPQCEnabled },
      context: { version: 'v3' },
      fetchPolicy: 'no-cache'
    },
    qlObjKey: 'asset'
  });

  async function setOrgInfo(asset) {
    let idToken = await getIdTokenClaims({
      audience: process.env.REACT_APP_AUTH0_AUDIENCE
    });
    setOrg(idToken[orgNamespace]);
    setOrgId(idToken[orgIdNamespace]);
    setComponent(asset);
  }

  useEffect(() => {
    if (!!component) {
      parseJSON(component);
    }
  }, [component, org]);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setOrgInfo(asset);
    } else if (!asset && !!componentId) {
      makeRequest({ variables: { args: { assetId: componentId }, includeQuantumCapable: isPQCEnabled }, context: { version: 'v3' } });
    }
  }, [open, isPQCEnabled]);

  useEffect(() => {
    if (assetData && !isEmpty(assetData)) {
      setOrgInfo(assetData);
    }
  }, [assetData]);

  function handleOnClose() {
    setOrg(null);
    setOrgId(null);
    setComponent(null);
    setParsedAnalysis({});
    handleClose();
  }

  function parseJSON(component) {
    let parsed = '';
    let { assetId, revision } = getDataFromTurbinePPComposedAssetId(component?.componentId || componentId);
    parsed = parsed.concat(
      `Asset Name: ${component.name || assetId},`,
      `Asset ID: ${assetId},`,
      `Revision: ${revision},`,
      !!component.sizeBytes ? `Asset Size: ${humanFileSize(component.sizeBytes)},` : '',
      !!org ? `Org: ${org},` : '',
      !!orgId ? `Org ID: ${orgId},` : '',
      !!component.createdAt
        ? `${component.status === IN_PROGRESS && !!component.firstAnalysisTime ? 'Initial ' : ''}Analysis started at: ${moment(
            new Date(component.createdAt)
          )?.format(ANALYSIS_LOG)},`
        : '',
      !!component.firstAnalysisTime
        ? `${component.status === IN_PROGRESS ? 'Initial ' : ''}Analysis completed at: ${moment(new Date(component.firstAnalysisTime))?.format(
            ANALYSIS_LOG
          )},`
        : '',
      component.status === COMPLETED || (component.status === FAILED && !!component.firstAnalysisTime) ? 'All Tasks Completed Successfully.,' : '',
      parsedAnalysisStatus(component),
      component.status === IN_PROGRESS && !!component.firstAnalysisTime
        ? `Analysis updated at: ${moment(new Date(component.updatedAt))?.format(ANALYSIS_LOG)}`
        : '',
      component.status === COMPLETED && !!component.firstAnalysisTime
        ? `Analysis updated at: ${moment(new Date(component.updatedAt))?.format(ANALYSIS_LOG)}`
        : '',
      component.status === IN_PROGRESS && !!component.firstAnalysisTime
        ? `,[ Analysis is updating ] (started ${moment(new Date(component.updatedAt)).fromNow()} )`
        : ''
    );
    setParsedAnalysis(parsed.replace(/ *, */g, '\n'));
  }

  const handleOnCopy = () => {
    navigator.clipboard.writeText(parsedAnalysis);
    enqueueSnackbar('Analysis log copied successfully', { variant: 'success', preventDuplicate: true, autoHideDuration: 3000 });
  };

  return (
    <Modal open={open} onClose={handleOnClose} aria-labelledby="analysis-log-title" aria-describedby="analysis-log-description">
      <ModalMainContainer width={'50%'}>
        <Card p={0} elevation={0}>
          <CardHeader
            sx={{ margin: 'unset', padding: theme.spacing(2, 3) }}
            title={
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box display={'flex'} alignItems={'center'}>
                  <Typography component="span" variant="h3">{`Analysis Log`}</Typography>
                </Box>
                <NRStatus fontSize={12} statusValue={component?.status} style={{ marginRight: '12px' }} />
              </Box>
            }
            action={
              <Tooltip title={CLOSE} placement="right">
                <IconButton aria-label="Close" onClick={handleOnClose}>
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
          <CardContent p={theme.spacing(3)}>
            {!!component && parsedAnalysis && !isEmpty(parsedAnalysis) ? (
              <>
                <Box sx={{ width: '100%', position: 'relative' }}>
                  <Tooltip title="Copy Analysis log">
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: 24,
                        backgroundColor: 'transparent',
                        color: theme.palette.text.iconAlt,
                        padding: theme.spacing(0.5),
                        display: 'flex',
                        cursor: 'pointer'
                      }}
                      aria-label="copy"
                      onClick={handleOnCopy}
                    >
                      <Copy />
                    </Box>
                  </Tooltip>
                </Box>
                <SyntaxHighlighter
                  customStyle={{ maxHeight: '60vh', margin: '16px 0', backgroundColor: theme.palette.neutral[800] }}
                  showLineNumbers={true}
                  wrapLongLines={true}
                  language="text"
                  style={a11yDark}
                >
                  {parsedAnalysis}
                </SyntaxHighlighter>
              </>
            ) : (
              <Box sx={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <NRLoader />
              </Box>
            )}
          </CardContent>
          <CardActions sx={{ padding: theme.spacing(1.75, 3), justifyContent: 'flex-end' }}>
            <Button variant="contained" color="secondary" onClick={handleOnClose}>
              {CLOSE}
            </Button>
          </CardActions>
        </Card>
      </ModalMainContainer>
    </Modal>
  );
}
export default NRAnalysisLog;
